import * as yup from 'yup';
import { keys } from 'lodash';
import { ALL_UNITS } from '@/features/units/schema';

const categories = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Dessert', 'Side'];

export const MIN_EQUIPMENTS = 0;
export const MAX_EQUIPMENTS = 20;

export const MIN_INSTRUCTION_GROUPS = 1;
export const MAX_INSTRUCTION_GROUPS = 10;

export const MIN_INSTRUCTIONS = 0;
export const MAX_INSTRUCTIONS = 200;

export const MIN_INGREDIENT_GROUPS = 1;
export const MAX_INGREDIENT_GROUPS = 10;

export const MIN_INGREDIENTS = 1;
export const MAX_INGREDIENTS = 200;

export const categoriesOptions = categories.map(category => ({
  value: category.toLowerCase(),
  label: category
}));

// Empty column means no explicit sort (URL has no sort arg); recipes still
// fall back to modified/desc when sorting.
export function newSort() {
  return { column: '', direction: 'desc' };
}

export function newRecipeFilters() {
  return {
    search: '',
    categories: [],
    tags: [],
    ingredients: [],
    favorites: false,
    tried: false,
    prepTime: null,
    cookTime: null,
    totalTime: null,
    calories: null,
  };
}

export const recipeIngredient = yup.object({
  // TODO: Switch to ingredient id
  name: yup.string().required('Ingredient name is required'),

  amount: yup.number().typeError('Amount must be a number').min(0, 'Amount must be at least 0').required('Amount is required'),  
  unit: yup.string().oneOf(ALL_UNITS).required('Unit is required'), 
});

const instruction = yup.object({  
  step: yup.string().required('Step description is required'),  
});

const equipment = yup.object({
  // TODO: Switch to equipment id
  name: yup.string().required('Equipment name is required'),
});

const image = yup.object({
  file_name: yup.string().required('Image file is required'),
  ext: yup.string().required('Image extension is required'),
  width: yup.number().optional(),
  sizes: yup.array().of(yup.number()).optional(),
  url: yup.string().optional(),
});

const detailsFields = {
  name: yup.string().required('Recipe name is required'),
  description: yup.string().optional(),  
  url: yup.string().url('Invalid URL').optional(),
  video_url: yup.string().url('Invalid URL').optional(),
  images: yup.array().of(image).required(),
  categories: yup.array().of(yup.string()).min(1, 'At least one category is required'),
  tags: yup.array().of(yup.string()).optional(),
  servings: yup.number().typeError('Servings must be a number').min(1, 'Servings must be at least 1').required('Servings are required'),
  prep_time: yup.number().typeError('Prep time must be a number').min(0, 'Prep time must be at least 0').required('Prep time is required'),
  cook_time: yup.number().typeError('Cook time must be a number').min(0, 'Cook time must be at least 0').required('Cook time is required'),  
} 

const ingredientsFields = {
  ingredient_groups: yup.array().of(yup.object({
    name: yup.string().required('Group name is required'),
    ingredients: yup.array().of(recipeIngredient).required().min(1, 'At least one ingredient is required'),
  })).required().min(MIN_INGREDIENT_GROUPS).max(MAX_INGREDIENT_GROUPS),
}

const instructionsFields = {
  // Drop blank default steps so recipes can be saved without instructions.
  instruction_groups: yup.array()
    .transform((groups) =>
      (groups || [])
        .map((group) => ({
          ...group,
          instructions: (group.instructions || []).filter((i) => i?.step?.trim()),
        }))
        .filter((group) => group.instructions.length > 0)
    )
    .of(yup.object({
      name: yup.string().required('Group name is required'),
      instructions: yup.array().of(instruction).required().min(MIN_INSTRUCTIONS).max(MAX_INSTRUCTIONS),
    }))
    .required()
    // UI always keeps one group (MIN_INSTRUCTION_GROUPS); after transform it may be empty, hence the min value needs to be zero.
    .min(0)
    .max(MAX_INSTRUCTION_GROUPS),
}

const equipmentFields = {
  equipment: yup.array().of(equipment).required().min(MIN_EQUIPMENTS).max(MAX_EQUIPMENTS),
}

export const recipeSchema = yup.object({ 
  ...detailsFields, 
  ...ingredientsFields, 
  ...instructionsFields, 
  ...equipmentFields 
});

export const fieldsByTab = [
  keys(detailsFields),
  keys(ingredientsFields),
  keys(instructionsFields),
  keys(equipmentFields),
];

export function newIngredient() {
  return {
    name: '',
    amount: 1,
    unit: 'g',
  }
}

export function newIngredientGroup() {
  return {
    name: '',
    ingredients: [newIngredient()],
  }
}

export function newInstruction() {
  return {
    step: '',
  }
}

export function newInstructionGroup() {
  return {
    name: '',
    instructions: [newInstruction()],
  }
}

export function defaultInstructionGroups() {
  return [{
    name: 'Instructions',
    instructions: [newInstruction()],
  }];
}

export function newEquipment() {
  return {
    name: '',
  }
}

export function newRecipe() {
  return {
    name: '',
    description: '',    
    url: '',
    video_url: '',
    categories: ['dinner'],
    tags: [],
    servings: 1,
    prep_time: 15,
    cook_time: 30,
    ingredient_groups: [{
      name: 'Ingredients',
      ingredients: [newIngredient()],
    }],
    instruction_groups: defaultInstructionGroups(),
    equipment: [],
    images: [],
  };
}

export function toRecipeFormData(recipe) {
  return {
    ...recipe,
    instruction_groups: recipe.instruction_groups?.length
      ? recipe.instruction_groups
      : defaultInstructionGroups(),
  };
}

export const fromTextSchema = yup.object({
  text: yup.string().required('Recipe text is required'),
});

export function newFromText() {
  return {
    text: '',
  }
}

export const fromURLSchema = yup.object({
  url: yup.string().url('Invalid URL').required('URL is required'),
});

export function newFromURL() {
  return {
    url: '',
  }
}